import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Download, ArrowLeft, ShieldCheck, Package, Clock, Check, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Settings → Privacy & Data → Export My VAULT.
 * Starts a background job and polls `/api/exports/{job_id}` for status.
 * The download link itself only arrives by email — the status endpoint
 * intentionally never returns the download key.
 */
export default function SettingsScreenDataExport() {
    const [job, setJob] = useState(null);
    const [starting, setStarting] = useState(false);

    // Poll while the current job is running/queued
    useEffect(() => {
        if (!job || ["ready", "failed"].includes(job.status)) return;
        const iv = setInterval(async () => {
            try {
                const r = await api.get(`/exports/${job.job_id || job.id}`);
                setJob({ ...r.data, job_id: job.job_id || job.id });
                if (r.data?.status === "ready") {
                    toast.success("Your VAULT export is ready — check your email.");
                    clearInterval(iv);
                }
                if (r.data?.status === "failed") {
                    toast.error("Export failed. Please try again.");
                    clearInterval(iv);
                }
            } catch { /* silent */ }
        }, 2500);
        return () => clearInterval(iv);
    }, [job?.job_id, job?.id, job?.status]);

    const startExport = async () => {
        setStarting(true);
        try {
            const r = await api.post("/exports/vault");
            setJob(r.data);
            toast.success("Export queued. You'll get an email when it's ready.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start export.");
        } finally {
            setStarting(false);
        }
    };

    const running = job && ["queued", "running"].includes(job.status);
    const ready = job?.status === "ready";
    const failed = job?.status === "failed";

    return (
        <div className="max-w-3xl mx-auto p-6 md:p-8" data-testid="settings-data-export">
            {/* Breadcrumb */}
            <div className="mb-6 flex items-center gap-2 text-xs">
                <Link to="/dashboard" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Dashboard
                </Link>
                <span className="text-stone-300">/</span>
                <span className="text-stone-500">Settings</span>
                <span className="text-stone-300">/</span>
                <span className="font-medium text-stone-900 dark:text-stone-100">Privacy &amp; Data</span>
                <span className="text-stone-300">/</span>
                <span className="font-medium text-stone-900 dark:text-stone-100">Export</span>
            </div>

            {/* Header */}
            <div className="flex items-start gap-3 mb-6">
                <div className="mt-1 rounded-full bg-amber-50 dark:bg-amber-900/30 p-1.5">
                    <Package size={16} className="text-amber-700 dark:text-amber-300" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Export my VAULT</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        Take everything you&apos;ve stored in VAULT with you. We&apos;ll assemble a
                        signed ZIP of every record in your workspace and email you a secure
                        download link — no third-party service touches the file.
                    </p>
                </div>
            </div>

            {/* Included / excluded */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/30 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-emerald-800 dark:text-emerald-300 mb-2">Included</div>
                    <ul className="text-[13px] text-stone-700 dark:text-stone-200 space-y-1">
                        <li>• Cards, banks, cash accounts</li>
                        <li>• Every expense, payment, statement</li>
                        <li>• Subscriptions, disputes, budgets</li>
                        <li>• Documents metadata &amp; audit logs</li>
                        <li>• Notifications &amp; preferences</li>
                    </ul>
                </div>
                <div className="rounded-xl border border-stone-200 bg-white dark:bg-stone-900 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-600 dark:text-stone-400 mb-2">Never in the archive</div>
                    <ul className="text-[13px] text-stone-700 dark:text-stone-200 space-y-1">
                        <li>• CVV, OTP, or full card numbers <i>(never stored)</i></li>
                        <li>• Passwords <i>(only bcrypt hashes, excluded)</i></li>
                        <li>• MFA secrets or recovery codes</li>
                        <li>• Other users&apos; workspaces</li>
                    </ul>
                </div>
            </div>

            {/* Action card */}
            <div className="rounded-2xl border border-stone-200 bg-white dark:bg-stone-900 p-6 mb-4">
                <div className="flex items-start gap-3">
                    <ShieldCheck size={18} className="text-emerald-600 mt-1 shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-stone-900 dark:text-stone-100">
                            Sends a secure link to your registered email
                        </div>
                        <div className="text-[12px] text-stone-500 mt-1">
                            The link is unique to your account and expires 24 hours after generation.
                        </div>
                    </div>
                    <button
                        onClick={startExport}
                        disabled={starting || running}
                        className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white px-5 py-2 text-sm font-semibold hover:bg-stone-700 disabled:opacity-50"
                        data-testid="export-start-btn"
                    >
                        <Download size={13} />
                        {starting ? "Starting…" : running ? "Preparing…" : "Export my VAULT"}
                    </button>
                </div>

                {/* Live status strip */}
                {job && (
                    <div
                        className={`mt-4 rounded-lg border p-3 text-[12.5px] flex items-start gap-2 ${
                            ready ? "border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-200" :
                            failed ? "border-rose-200 bg-rose-50 text-rose-900" :
                            "border-stone-200 bg-stone-50 dark:bg-stone-800 text-stone-700 dark:text-stone-200"
                        }`}
                        data-testid={`export-status-${job.status}`}
                    >
                        {ready ? <Check size={14} className="mt-0.5" /> :
                         failed ? <AlertCircle size={14} className="mt-0.5" /> :
                         <Clock size={14} className="mt-0.5 animate-pulse" />}
                        <div className="flex-1">
                            {ready ? (
                                <>
                                    <b>Archive ready.</b> Check your inbox for the download link. Rows exported:{" "}
                                    <b>{job.row_count?.toLocaleString?.() || 0}</b>.
                                </>
                            ) : failed ? (
                                <><b>Export failed.</b> {job.error || "Please try again in a moment."}</>
                            ) : (
                                <>
                                    <b>Preparing your archive…</b> This usually takes under 30 seconds.
                                    You can safely leave this page — we&apos;ll email you when it&apos;s done.
                                </>
                            )}
                        </div>
                        <span className="text-[10px] uppercase tracking-widest opacity-70">Job {String(job.id || job.job_id).slice(0, 8)}</span>
                    </div>
                )}
            </div>

            {/* Legal */}
            <div className="text-[11px] text-stone-500 max-w-2xl">
                By exporting you affirm you are the account holder. VAULT logs the request in your audit
                trail so you can see who exported what, and when. See{" "}
                <Link to="/legal/privacy" className="underline text-stone-700 dark:text-stone-300">Privacy Policy</Link>.
            </div>
        </div>
    );
}
